import { LayoutModule } from '@angular/cdk/layout'
import { APP_BASE_HREF, CommonModule } from '@angular/common'
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
  withXsrfConfiguration,
} from '@angular/common/http'
import type { ApplicationConfig, ApplicationRef, Type } from '@angular/core'
import { ErrorHandler, importProvidersFrom, provideZoneChangeDetection } from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import {
  bootstrapApplication as bootstrapAngularApplication,
  BrowserModule,
  HammerModule,
  Meta,
} from '@angular/platform-browser'
import { provideAnimations } from '@angular/platform-browser/animations'
import { RouteReuseStrategy } from '@angular/router'
import { EffectsModule } from '@ngrx/effects'
import { INITIAL_STATE, StoreModule } from '@ngrx/store'
import { StoreDevtoolsModule } from '@ngrx/store-devtools'
import { StorageModule } from '@ngx-pwa/local-storage'

import { AccountModule } from '@account/account.module'
import { effects } from '@store/root/effects'
import { getInitialState, metaReducers, ROOT_REDUCERS } from '@store/root/reducers'
import { UIModule } from '@ui/ui.module'

import { apiUrlInterceptor } from './interceptors/api-url-interceptor'
import { defaultHeadersInterceptor } from './interceptors/default-headers-interceptor'
import { requestMonitorInterceptor } from './interceptors/request-monitor-interceptor'
import { SbInterceptor } from './interceptors/sb-interceptor'
import { Dict } from './models/dict'
import { CustomFormsModule } from './modules/forms/forms.module'
import { ProfileModule } from './modules/profile/profile.module'
import { CustomRoutesReuseStrategy } from './modules/routing/custom-routes-reuse-strategy'
import { openReplayInterceptor } from './modules/tracking/interceptors/open-replay-interceptor'
import { trackingHeadersInterceptor } from './modules/tracking/interceptors/tracking-headers-interceptor'
import { PushNotificationsModule } from './modules/push-notifications/push-notifications.module'
import { ErrorHandlers } from './services/error-handlers.service'
import { sessionStorageFactory } from './services/session-storage.provider'
import { SessionStorage } from './services/session-storage.service'

const FRAMEWORK_PROVIDERS: ApplicationConfig['providers'] = [
  provideZoneChangeDetection(),
  importProvidersFrom(
    BrowserModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    StorageModule.forRoot({ IDBNoWrap: true }),
    LayoutModule,
    AccountModule,
    StoreModule.forRoot(ROOT_REDUCERS, { metaReducers }),
    StoreDevtoolsModule.instrument({
      // TODO: add limit for prod env
      maxAge: 25,
      connectInZone: true,
    }),
    EffectsModule.forRoot(effects),
    UIModule,
    ProfileModule,
    CustomFormsModule,
    PushNotificationsModule,
    HammerModule,
  ),
  { provide: APP_BASE_HREF, useValue: '/' },
  { provide: INITIAL_STATE, useFactory: getInitialState },
  {
    provide: SessionStorage,
    useFactory: sessionStorageFactory,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: SbInterceptor,
    multi: true,
  },
  { provide: RouteReuseStrategy, useClass: CustomRoutesReuseStrategy },
  { provide: ErrorHandler, useExisting: ErrorHandlers },
  Meta,
  Dict,
  provideHttpClient(
    withInterceptors([
      apiUrlInterceptor,
      defaultHeadersInterceptor,
      openReplayInterceptor,
      trackingHeadersInterceptor,
      requestMonitorInterceptor,
    ]),
    withInterceptorsFromDi(),
    withXsrfConfiguration({
      cookieName: 'CSRF-TOKEN',
      headerName: 'X-CSRF-TOKEN',
    }),
  ),
  provideAnimations(),
]

export function bootstrapApplication(
  rootComponent: Type<unknown>,
  options?: ApplicationConfig,
): Promise<ApplicationRef> {
  return bootstrapAngularApplication(rootComponent, {
    ...options,
    providers: [...FRAMEWORK_PROVIDERS, ...(options?.providers ?? [])],
  }).catch(ErrorHandlers.handleError) as Promise<ApplicationRef>
}
